import { useState, useMemo } from 'react';
import {
    Container,
    Typography,
    Box,
    Tab,
    Tabs,
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Stack,
    Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DefaultLayout from '../layouts/DefaultLayout';
import { usePermissions } from '../hooks/usePermissions';
import PermissionDenied from '../components/PermissionDenied';
import GlobalHeader from '../components/GlobalHeader';
import TabPanel from '../components/TabPanel';
import GlobalInventoryList from '../components/GlobalInventoryList';
import ItemCategoryManager from '../components/ItemCategoryManager';
import StorageLocationManager from '../components/StorageLocationManager';
import ItemTypeForm from '../components/ItemTypeForm';
import ItemTypeDetail from '../components/ItemTypeDetail';
import RecordInventoryChangeDialog from '../components/RecordInventoryChangeDialog';
import ActiveInventoryContent from '../components/ActiveInventoryContent';
import { db } from '../instant';
import { ItemType, queryItemTypes } from '../schema';
import { INVENTORY_ITEM_QUERY_PARAM } from '../constants';
import { createItemType, deleteItemType } from '../utils/itemTypeUtils';
import { calculateItemLocations } from '../utils/inventory';
import { usePadding } from '../spacing';

function InventoryFab({ onClick }: { onClick: () => void }) {
    const { bottomPadding } = usePadding();
    return (
        <Fab color="primary" aria-label="add item" sx={{ position: 'fixed', bottom: bottomPadding + 32, right: 32 }} onClick={onClick}>
            <AddIcon />
        </Fab>
    );
}

const INVENTORY_TAB_ITEMS = 0;
const INVENTORY_TAB_CATEGORIES = 1;
const INVENTORY_TAB_STORAGE = 2;
const INVENTORY_TAB_EVENTS = 3;

export default function GlobalInventoryScreen() {
    const { canViewInventory, isLoading: permissionsLoading } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const activeTab = pathname.endsWith('/events')
        ? INVENTORY_TAB_EVENTS
        : pathname.endsWith('/categories')
          ? INVENTORY_TAB_CATEGORIES
          : pathname.endsWith('/storage')
            ? INVENTORY_TAB_STORAGE
            : pathname.endsWith('/items')
              ? INVENTORY_TAB_ITEMS
              : INVENTORY_TAB_ITEMS;
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingItemType, setEditingItemType] = useState<ItemType | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ItemType | undefined>(undefined);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);
    const [recordDialogOpen, setRecordDialogOpen] = useState(false);
    const [recordDialogMode, setRecordDialogMode] = useState<'purchase' | 'lostOrBroken' | 'adjustment'>('purchase');
    const [itemForRecord, setItemForRecord] = useState<ItemType | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const { data: itemTypesData } = queryItemTypes();
    const [searchParams, setSearchParams] = useSearchParams();
    const itemIdFromQuery = searchParams.get(INVENTORY_ITEM_QUERY_PARAM);

    const stripItemQueryParamIfPresent = () => {
        if (searchParams.get(INVENTORY_ITEM_QUERY_PARAM)) {
            const next = new URLSearchParams(searchParams);
            next.delete(INVENTORY_ITEM_QUERY_PARAM);
            setSearchParams(next, { replace: true });
        }
    };

    const itemFromQuery = useMemo(() => {
        if (!itemIdFromQuery || !itemTypesData?.itemTypes) return null;
        return itemTypesData.itemTypes.find((t: ItemType) => t.id === itemIdFromQuery) ?? null;
    }, [itemIdFromQuery, itemTypesData?.itemTypes]);

    const detailItemType = selectedItemType ?? itemFromQuery;
    const detailDialogOpenCombined = detailDialogOpen || itemFromQuery != null;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const path =
            newValue === INVENTORY_TAB_EVENTS
                ? '/inventory/events'
                : newValue === INVENTORY_TAB_CATEGORIES
                  ? '/inventory/categories'
                  : newValue === INVENTORY_TAB_STORAGE
                    ? '/inventory/storage'
                    : '/inventory/items';
        navigate(path);
    };

    const handleCreate = () => {
        setEditingItemType(undefined);
        setFormDialogOpen(true);
    };

    const handleEdit = (itemType: ItemType) => {
        setEditingItemType(itemType);
        setFormDialogOpen(true);
    };

    const { user } = db.useAuth();

    if (!permissionsLoading && !canViewInventory) {
        return <PermissionDenied requiredPermission="View Inventory" />;
    }

    const handleFormSubmit = async (values: {
        name: string;
        description: string;
        categoryId: string;
        photoUrl?: string;
        consumable: boolean;
        photo?: File | null;
    }) => {
        try {
            const token = user?.refresh_token;
            await createItemType(
                {
                    name: values.name,
                    description: values.description || '',
                    categoryId: values.categoryId,
                    photoUrl: values.photoUrl,
                    consumable: values.consumable || false,
                    photo: values.photo || null,
                    token,
                },
                editingItemType
            );
            setFormDialogOpen(false);
            setEditingItemType(undefined);
        } catch (error) {
            console.error('Failed to save item type:', error);
            throw error;
        }
    };

    const handleFormCancel = () => {
        setFormDialogOpen(false);
        setEditingItemType(undefined);
    };

    const handleDelete = (itemType: ItemType) => {
        setItemToDelete(itemType);
        setDeleteError(null);
        setDeleteDialogOpen(true);
        setDetailDialogOpen(false);
    };

    const handleViewDetails = (itemType: ItemType) => {
        setSelectedItemType(itemType);
        setDetailDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        try {
            setDeleteError(null);
            await deleteItemType(itemToDelete);
            setDeleteDialogOpen(false);
            setItemToDelete(undefined);
        } catch (error: any) {
            setDeleteError(error.message || 'Failed to delete item');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(undefined);
        setDeleteError(null);
    };

    const locationData = itemToDelete
        ? calculateItemLocations(
              itemToDelete,
              'itemLocations' in itemToDelete
                  ? ((itemToDelete.itemLocations || []) as Array<{
                        id: string;
                        quantity: number;
                        event: { id: string; name: string } | null | undefined;
                    }>)
                  : []
          )
        : null;
    const canDelete = locationData && locationData.total === 0;

    const handleRecordPurchase = (itemType: ItemType) => {
        setItemForRecord(itemType);
        setRecordDialogMode('purchase');
        setRecordDialogOpen(true);
    };

    const handleRecordLostOrBroken = (itemType: ItemType) => {
        setItemForRecord(itemType);
        setRecordDialogMode('lostOrBroken');
        setRecordDialogOpen(true);
    };

    const handleAdjustQuantity = (itemType: ItemType) => {
        setItemForRecord(itemType);
        setRecordDialogMode('adjustment');
        setRecordDialogOpen(true);
    };

    const handleRecordConsumption = (_itemType: ItemType) => {
        // Consumption recording requires an event context
        // For now, show a message - can be enhanced later to allow event selection
        alert(
            'Consumption recording requires an event context. Please navigate to an event and use the Consumables section to record consumption.'
        );
    };

    const handleRecordDialogClose = () => {
        setRecordDialogOpen(false);
        setItemForRecord(null);
    };

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <GlobalHeader title="Inventory" />
                <Container maxWidth="lg" disableGutters sx={{ flexGrow: 1, py: 3, px: { xs: 2, sm: 3 }, minWidth: 0, maxWidth: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, minWidth: 0 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="inventory tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab label="Items" id="inventory-tab-0" aria-controls="inventory-tabpanel-0" />
                            <Tab label="Categories" id="inventory-tab-1" aria-controls="inventory-tabpanel-1" />
                            <Tab label="Storage locations" id="inventory-tab-2" aria-controls="inventory-tabpanel-2" />
                            <Tab label="Active events" id="inventory-tab-3" aria-controls="inventory-tabpanel-3" />
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={INVENTORY_TAB_ITEMS} idPrefix="inventory">
                        <GlobalInventoryList
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onViewDetails={handleViewDetails}
                            onRecordPurchase={handleRecordPurchase}
                            onRecordLostOrBroken={handleRecordLostOrBroken}
                            onRecordConsumption={handleRecordConsumption}
                            onAdjustQuantity={handleAdjustQuantity}
                            onAddItem={handleCreate}
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={INVENTORY_TAB_CATEGORIES} idPrefix="inventory">
                        <ItemCategoryManager />
                    </TabPanel>

                    <TabPanel value={activeTab} index={INVENTORY_TAB_STORAGE} idPrefix="inventory">
                        <StorageLocationManager />
                    </TabPanel>

                    <TabPanel value={activeTab} index={INVENTORY_TAB_EVENTS} idPrefix="inventory">
                        <ActiveInventoryContent />
                    </TabPanel>

                    {activeTab === INVENTORY_TAB_ITEMS && <InventoryFab onClick={handleCreate} />}

                    <ItemTypeForm
                        itemType={editingItemType}
                        onSubmit={handleFormSubmit}
                        onCancel={handleFormCancel}
                        open={formDialogOpen}
                    />

                    {/* Delete Confirmation Dialog */}
                    <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                        <DialogTitle>Delete Item</DialogTitle>
                        <DialogContent>
                            <Stack spacing={2}>
                                {canDelete ? (
                                    <>
                                        <Typography>
                                            Are you sure you want to delete &quot;{itemToDelete?.name}&quot;? This action cannot be undone.
                                        </Typography>
                                        <Alert severity="warning">This will delete the item and all related history.</Alert>
                                    </>
                                ) : (
                                    <>
                                        <Alert severity="error">Cannot delete item with inventory</Alert>
                                        <Typography>This item has inventory that must be removed first.</Typography>
                                        {locationData && (
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    Current inventory:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {locationData.storageLocations.map((s, i) => (
                                                        <span key={s.id}>
                                                            {s.quantity} at {s.name}
                                                            {i < locationData.storageLocations.length - 1 || locationData.events.length > 0
                                                                ? ', '
                                                                : ''}
                                                        </span>
                                                    ))}
                                                    {locationData.events.map((e, i) => (
                                                        <span key={e.eventId}>
                                                            {e.quantity} at {e.eventName}
                                                            {i < locationData.events.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ))}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            Please move or remove all inventory first.
                                        </Typography>
                                    </>
                                )}
                                {deleteError && <Alert severity="error">{deleteError}</Alert>}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleDeleteCancel}>Close</Button>
                            {canDelete && (
                                <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                                    Delete
                                </Button>
                            )}
                        </DialogActions>
                    </Dialog>

                    {/* Item Detail Modal */}
                    <ItemTypeDetail
                        itemType={detailItemType}
                        open={detailDialogOpenCombined}
                        onClose={() => {
                            setDetailDialogOpen(false);
                            setSelectedItemType(null);
                            stripItemQueryParamIfPresent();
                        }}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onRecordPurchase={handleRecordPurchase}
                        onRecordLostOrBroken={handleRecordLostOrBroken}
                        onRecordConsumption={handleRecordConsumption}
                        onAdjustQuantity={handleAdjustQuantity}
                    />

                    {/* Record Inventory Change Dialog */}
                    <RecordInventoryChangeDialog
                        open={recordDialogOpen}
                        onClose={handleRecordDialogClose}
                        itemType={itemForRecord}
                        mode={recordDialogMode}
                    />

                    <Snackbar
                        open={!!successMessage}
                        autoHideDuration={3000}
                        onClose={() => setSuccessMessage(null)}
                        message={successMessage}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    />
                </Container>
            </Box>
        </DefaultLayout>
    );
}
